import type { EstimateAssumption, EstimateExclusion, EstimateWarning, EstimatorInput } from "../../types/index.js";
import { getScopeMultiplier } from "../scope-multipliers.js";
import {
  applyItem,
  findItem,
  scaleAppliedItem,
  type AppliedLineItem,
  type ProjectCalculationOutput,
  type ProjectCalculatorFn
} from "./shared.js";

/**
 * The Home Addition Estimator (Phase 12). Per the brief, an addition is
 * treated as a HIGH-COMPLEXITY project type: unlike a kitchen or basement
 * remodel confined to an existing room, an addition also has to
 * integrate with the existing home's structure, roofline, and site -
 * factors a self-reported questionnaire fundamentally cannot assess.
 * This calculator follows the same tiered, fully-gated pattern kitchen
 * (Phase 5A) and basement (Phase 8) established: base shell construction
 * is priced separately from every individual system/finish category, so
 * a fully-loaded addition produces 15+ independently-priced line items
 * rather than one sqft multiplier - see
 * packages/config/src/questions/addition.ts and
 * docs/addition-estimator.md.
 *
 * Per the brief's explicit instruction, this calculator never presents
 * the result as highly precise: it always attaches standing warnings
 * about site-condition and structural-integration uncertainty
 * (see the "special handling" section below), regardless of which
 * answers were given, and defaults several categories with an explicit
 * "_defaulted" assumption (which `input-completeness.ts` picks up
 * automatically) rather than silently guessing at zero cost.
 */

const KITCHEN_ITEM_BY_SCOPE: Record<string, string | null> = {
  none: null,
  renovate_existing: "addition_kitchen_renovate_existing",
  new_kitchen: "addition_kitchen_new"
};

const BATHROOM_ITEM_BY_SCOPE: Record<string, string | null> = {
  none: null,
  one_bathroom: "addition_bathroom_one",
  multiple_bathrooms: "addition_bathroom_multiple"
};

/** Assumed bathroom count when "Two or more bathrooms" is selected without a specific number - see the corresponding assumption pushed below. */
const ASSUMED_MULTIPLE_BATHROOM_COUNT = 2;

const STRUCTURAL_ITEM_BY_COMPLEXITY: Record<string, string> = {
  standard: "addition_structural_standard",
  moderate: "addition_structural_moderate",
  complex: "addition_structural_complex"
};

const HVAC_ITEM_BY_SCOPE: Record<string, string> = {
  extend_existing: "addition_hvac_extend",
  dedicated_system: "addition_hvac_dedicated"
};

const ELECTRICAL_ITEM_BY_SCOPE: Record<string, string> = {
  standard_wiring: "addition_electrical_standard",
  panel_upgrade_required: "addition_electrical_panel_upgrade"
};

const PLUMBING_ITEM_BY_SCOPE: Record<string, string | null> = {
  none_no_plumbing: null,
  extend_existing_lines: "addition_plumbing_extend",
  new_dedicated_lines: "addition_plumbing_new_lines"
};

const WINDOWS_DOORS_ITEM_BY_TIER: Record<string, string> = {
  standard: "addition_windows_doors_standard",
  upgraded: "addition_windows_doors_upgraded",
  premium: "addition_windows_doors_premium"
};

const ROOFING_ITEM_BY_TYPE: Record<string, string> = {
  tie_into_existing_roofline: "addition_roofing_tie_in",
  new_roof_section: "addition_roofing_new_section",
  flat_roof: "addition_roofing_flat"
};

/** Roofing items priced per sqft (vs. tie_in, which is a flat allowance). */
const ROOFING_SQFT_ITEMS = new Set(["addition_roofing_new_section", "addition_roofing_flat"]);

const EXTERIOR_FINISH_ITEM_BY_TIER: Record<string, string> = {
  match_existing: "addition_exterior_finish_match",
  vinyl_siding: "addition_exterior_finish_vinyl",
  brick_stone: "addition_exterior_finish_brick_stone",
  premium_finish: "addition_exterior_finish_premium"
};

const FOUNDATION_ITEM_BY_TYPE: Record<string, string | null> = {
  none_existing_structure: null,
  slab_on_grade: "addition_foundation_slab",
  crawl_space: "addition_foundation_crawl_space",
  full_foundation_basement: "addition_foundation_full_basement"
};

function rawString(input: EstimatorInput, key: string): string | undefined {
  const value = input.rawAnswers[key];
  return typeof value === "string" ? value : undefined;
}

function rawNumber(input: EstimatorInput, key: string): number | undefined {
  const value = input.rawAnswers[key];
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

export const calculateAddition: ProjectCalculatorFn = (input, catalog, regionalMultiplier) => {
  const applied: AppliedLineItem[] = [];
  const assumptions: EstimateAssumption[] = [];
  const exclusions: EstimateExclusion[] = [];
  const warnings: EstimateWarning[] = [];
  let anchorMissing = false;

  const finishLevel = input.finishLevel;
  const sqft = input.roomSize?.value;

  // --- Base shell construction (anchor) + scope adjustment ----------------
  let baseTypicalForPercentage = 0;
  if (sqft && sqft > 0) {
    const base = applyItem(catalog, "addition_base_construction", { quantity: sqft, finishLevel, regionalMultiplier });
    if (base) {
      const scopeMultiplier = getScopeMultiplier(input.scope);
      const scoped = scaleAppliedItem(base, scopeMultiplier);
      applied.push(scoped);
      baseTypicalForPercentage = scoped.result.typical?.amount ?? 0;
      assumptions.push({
        code: "scope_multiplier_applied",
        message: `Base construction cost adjusted by a ${scopeMultiplier}x scope multiplier for "${input.scope}".`
      });
    }

    // --- Second-storey structural premium ---------------------------------
    const storeys = rawString(input, "addition_storeys");
    if (storeys === "second_storey_addition") {
      const premium = applyItem(catalog, "addition_second_storey_premium", {
        regionalMultiplier,
        baseAmountForPercentage: baseTypicalForPercentage
      });
      if (premium) applied.push(premium);
      warnings.push({
        code: "addition_second_storey_structural_review",
        message:
          "A second storey built over existing structure requires an engineer to confirm the existing floor/roof/foundation can support the added load before any pricing here can be considered reliable.",
        severity: "caution"
      });
    }

    // --- Windows/doors (priced against addition footprint) -----------------
    const windowsDoorsTier = rawString(input, "addition_windows_doors_scope");
    const windowsDoorsItemId = windowsDoorsTier ? WINDOWS_DOORS_ITEM_BY_TIER[windowsDoorsTier] : "addition_windows_doors_standard";
    if (windowsDoorsItemId) {
      const windowsDoors = applyItem(catalog, windowsDoorsItemId, { quantity: sqft, finishLevel, regionalMultiplier });
      if (windowsDoors) {
        applied.push(windowsDoors);
        assumptions.push({
          code: "windows_doors_area_proxy",
          message: "Windows/doors cost is priced per square foot of the addition's overall footprint, as a planning-level proxy for actual glazed/door area, not a direct measurement."
        });
      }
      if (!windowsDoorsTier) {
        assumptions.push({ code: "windows_doors_scope_defaulted", message: "No windows/doors selection was provided - assumed a standard tier." });
      }
    }

    // --- Roofing -------------------------------------------------------------
    const roofingType = rawString(input, "addition_roofing_type") ?? "tie_into_existing_roofline";
    const roofingItemId = ROOFING_ITEM_BY_TYPE[roofingType];
    if (roofingItemId) {
      const roofing = applyItem(
        catalog,
        roofingItemId,
        ROOFING_SQFT_ITEMS.has(roofingItemId) ? { quantity: sqft, regionalMultiplier } : { regionalMultiplier }
      );
      if (roofing) applied.push(roofing);
      if (!rawString(input, "addition_roofing_type")) {
        assumptions.push({ code: "roofing_type_defaulted", message: "No roofing approach was provided - assumed tying into the existing roofline." });
      }
    }

    // --- Exterior finish -------------------------------------------------
    const exteriorFinishTier = rawString(input, "addition_exterior_finish") ?? "match_existing";
    const exteriorFinishItemId = EXTERIOR_FINISH_ITEM_BY_TIER[exteriorFinishTier];
    if (exteriorFinishItemId) {
      const exteriorFinish = applyItem(catalog, exteriorFinishItemId, { quantity: sqft, finishLevel, regionalMultiplier });
      if (exteriorFinish) {
        applied.push(exteriorFinish);
        assumptions.push({
          code: "exterior_finish_area_proxy",
          message: "Exterior finish cost is priced per square foot of the addition's overall footprint, as a planning-level proxy for actual finished surface area, not a direct measurement."
        });
      }
      if (!rawString(input, "addition_exterior_finish")) {
        assumptions.push({ code: "exterior_finish_defaulted", message: "No exterior finish was provided - assumed matching the existing exterior." });
      }
    }
  } else {
    anchorMissing = true;
  }

  // --- Foundation (tiered, no silent default - excluded when unknown) ------
  const foundationType = rawString(input, "addition_foundation_type");
  const foundationItemId = foundationType ? FOUNDATION_ITEM_BY_TYPE[foundationType] : undefined;
  if (foundationType === "none_existing_structure") {
    exclusions.push({
      code: "excludes_addition_foundation",
      message: "No new foundation was indicated (built over existing structure) - excluded from this estimate."
    });
  } else if (foundationItemId && sqft && sqft > 0) {
    const foundation = applyItem(catalog, foundationItemId, { quantity: sqft, regionalMultiplier });
    if (foundation) applied.push(foundation);
  } else {
    warnings.push({
      code: "addition_foundation_type_unconfirmed",
      message:
        "Foundation type was not confirmed. Foundation is one of the largest cost variables for an addition and depends on soil conditions and the existing home's grade - this estimate excludes a foundation line item until the type is confirmed, which means the real cost is very likely higher than shown.",
      severity: "blocking"
    });
    exclusions.push({
      code: "excludes_addition_foundation",
      message: "Foundation type was not confirmed - excluded from this estimate pending confirmation."
    });
  }

  // --- Kitchen (tiered) -----------------------------------------------------
  const kitchenScope = rawString(input, "addition_kitchen_scope") ?? "none";
  const kitchenItemId = KITCHEN_ITEM_BY_SCOPE[kitchenScope];
  if (kitchenItemId) {
    const kitchen = applyItem(catalog, kitchenItemId, { finishLevel, regionalMultiplier });
    if (kitchen) applied.push(kitchen);
  } else {
    exclusions.push({ code: "excludes_addition_kitchen", message: "No kitchen work was requested for the addition - excluded from this estimate." });
  }

  // --- Bathroom(s) (tiered) --------------------------------------------------
  const bathroomScope = rawString(input, "addition_bathroom_scope") ?? "none";
  const bathroomItemId = BATHROOM_ITEM_BY_SCOPE[bathroomScope];
  if (bathroomItemId) {
    const quantity = bathroomScope === "multiple_bathrooms" ? ASSUMED_MULTIPLE_BATHROOM_COUNT : 1;
    const bathroom = applyItem(catalog, bathroomItemId, { quantity, finishLevel, regionalMultiplier });
    if (bathroom) applied.push(bathroom);
    if (bathroomScope === "multiple_bathrooms") {
      assumptions.push({
        code: "bathroom_count_assumed",
        message: `"Two or more bathrooms" was selected without a specific count - assumed ${ASSUMED_MULTIPLE_BATHROOM_COUNT} bathrooms for this estimate.`
      });
    }
  } else {
    exclusions.push({ code: "excludes_addition_bathroom", message: "No bathroom work was requested for the addition - excluded from this estimate." });
  }

  // --- Bedrooms (counted) -----------------------------------------------------
  const bedroomCount = rawNumber(input, "addition_bedrooms");
  if (bedroomCount && bedroomCount > 0) {
    const bedroom = applyItem(catalog, "addition_bedroom", { quantity: bedroomCount, finishLevel, regionalMultiplier });
    if (bedroom) applied.push(bedroom);
  } else {
    exclusions.push({ code: "excludes_addition_bedrooms", message: "No bedrooms were indicated for the addition - excluded from this estimate." });
  }

  // --- Structural complexity (tiered - always priced) ------------------------
  const structuralComplexity = rawString(input, "addition_structural_complexity") ?? "moderate";
  const structuralItemId = STRUCTURAL_ITEM_BY_COMPLEXITY[structuralComplexity] ?? STRUCTURAL_ITEM_BY_COMPLEXITY["moderate"]!;
  const structural = applyItem(catalog, structuralItemId, { regionalMultiplier });
  if (structural) applied.push(structural);
  if (!rawString(input, "addition_structural_complexity")) {
    assumptions.push({
      code: "structural_complexity_defaulted",
      message: 'No structural complexity was provided - assumed "moderate" complexity, which may understate cost for a more involved tie-in.'
    });
  }

  // --- HVAC (tiered) -----------------------------------------------------------
  const hvacScope = rawString(input, "addition_hvac_scope");
  const resolvedHvacScope = hvacScope && hvacScope !== "unsure" ? hvacScope : "extend_existing";
  const hvacItemId = HVAC_ITEM_BY_SCOPE[resolvedHvacScope];
  if (hvacItemId) {
    const hvac = applyItem(catalog, hvacItemId, { finishLevel, regionalMultiplier });
    if (hvac) applied.push(hvac);
  }
  if (!hvacScope || hvacScope === "unsure") {
    assumptions.push({ code: "hvac_scope_defaulted", message: 'No HVAC scope was confirmed - assumed extending the existing system, which may understate cost if a dedicated system is actually needed.' });
  }

  // --- Electrical (tiered) -------------------------------------------------
  const electricalScope = rawString(input, "addition_electrical_scope");
  const resolvedElectricalScope = electricalScope && electricalScope !== "not_sure" ? electricalScope : "standard_wiring";
  const electricalItemId = ELECTRICAL_ITEM_BY_SCOPE[resolvedElectricalScope];
  if (electricalItemId) {
    const electrical = applyItem(catalog, electricalItemId, { finishLevel, regionalMultiplier });
    if (electrical) applied.push(electrical);
  }
  if (!electricalScope || electricalScope === "not_sure") {
    assumptions.push({
      code: "electrical_scope_defaulted",
      message: 'No electrical scope was confirmed - assumed standard new wiring with no panel upgrade, which may understate cost if a panel upgrade is actually required.'
    });
  }

  // --- Plumbing (tiered, defaults based on whether wet rooms were selected) ---
  const plumbingScope = rawString(input, "addition_plumbing_scope");
  const needsWetRoom = kitchenScope !== "none" || bathroomScope !== "none";
  const resolvedPlumbingScope = plumbingScope ?? (needsWetRoom ? "new_dedicated_lines" : "none_no_plumbing");
  const plumbingItemId = PLUMBING_ITEM_BY_SCOPE[resolvedPlumbingScope];
  if (plumbingItemId) {
    const plumbing = applyItem(catalog, plumbingItemId, { finishLevel, regionalMultiplier });
    if (plumbing) applied.push(plumbing);
  } else {
    exclusions.push({ code: "excludes_addition_plumbing", message: "No plumbing work was requested for the addition - excluded from this estimate." });
  }
  if (!plumbingScope) {
    assumptions.push({
      code: "plumbing_scope_defaulted",
      message: needsWetRoom
        ? "No plumbing scope was confirmed - assumed new dedicated supply/drain lines given the kitchen/bathroom selections."
        : "No plumbing scope was confirmed - assumed none needed, since no kitchen or bathroom was selected for the addition."
    });
  }

  // --- Design/architectural services -----------------------------------------
  const designServices = rawString(input, "addition_design_services");
  if (designServices === "yes" || designServices === "unknown" || !designServices) {
    const design = applyItem(catalog, "addition_design_architectural", { regionalMultiplier });
    if (design) applied.push(design);
  } else {
    exclusions.push({ code: "excludes_addition_design_services", message: "Design/architectural drawings were indicated as already in hand - excluded from this estimate." });
  }

  // --- Engineering -------------------------------------------------------------
  const engineeringRequired = rawString(input, "addition_engineering_required");
  if (engineeringRequired === "yes" || engineeringRequired === "unknown" || !engineeringRequired) {
    const engineering = applyItem(catalog, "addition_engineering", { regionalMultiplier });
    if (engineering) applied.push(engineering);
  } else {
    warnings.push({
      code: "addition_engineering_may_still_be_required",
      message:
        'Structural engineering was marked as not required, but almost all additions need at least some engineered drawings (e.g. for footings or a structural tie-in). Confirm this with your contractor or municipality - if engineering turns out to be required, this estimate is understated.',
      severity: "caution"
    });
    exclusions.push({ code: "excludes_addition_engineering", message: "Structural engineering was indicated as not required - excluded from this estimate." });
  }

  // --- Permit allowance (near-universal for a physical addition) -------------
  if (input.designAndPermits.permitLikelyRequired !== "no") {
    const permit = applyItem(catalog, "addition_permit_allowance", { regionalMultiplier });
    if (permit) applied.push(permit);
  } else {
    warnings.push({
      code: "addition_permit_likely_required_anyway",
      message:
        'A building permit is almost always required for a physical addition in Canada, even though "No" was selected for permit requirements. This estimate excludes a permit allowance as requested, but confirm with your municipality before proceeding - building without a required permit can create serious problems at resale or insurance time.',
      severity: "caution"
    });
    exclusions.push({ code: "excludes_addition_permit", message: "Permit was indicated as not required - excluded from this estimate." });
  }

  // --- Demolition ---------------------------------------------------------------
  if (input.workType.demolitionRequired === "yes" && findItem(catalog, "addition_demolition")) {
    const demolition = applyItem(catalog, "addition_demolition", { regionalMultiplier, baseAmountForPercentage: baseTypicalForPercentage });
    if (demolition) applied.push(demolition);
  }

  // --- Accessibility --------------------------------------------------------------
  if (input.accessibility.required === "yes" && findItem(catalog, "addition_accessibility")) {
    const accessibility = applyItem(catalog, "addition_accessibility", { finishLevel, regionalMultiplier });
    if (accessibility) applied.push(accessibility);
  }

  // --- Special handling: standing, addition-specific disclosures --------------
  // Per the Phase 12 brief, an addition estimate must never be presented
  // as highly precise - these are always attached, regardless of answers,
  // since neither of them can be resolved from a self-reported
  // questionnaire alone.
  warnings.push({
    code: "addition_site_conditions_vary",
    message:
      "Site conditions - soil composition and drainage, existing foundation depth, utility locations, property setbacks, and site access - cannot be assessed from a questionnaire, and can materially change the cost of an addition. This estimate assumes normal, unobstructed site conditions; a site visit and, where a new foundation is involved, a geotechnical/soil assessment may be needed before finalizing budget.",
    severity: "caution"
  });
  warnings.push({
    code: "addition_structural_integration_varies",
    message:
      "Tying a new addition into an existing home's roof, walls, and foundation carries integration risk that varies significantly from house to house and cannot be fully captured by a questionnaire, regardless of the structural complexity selected. A structural engineer's assessment is strongly recommended before finalizing budget.",
    severity: "caution"
  });
  assumptions.push({
    code: "addition_planning_estimate_only",
    message:
      "This is a planning-level estimate for a high-complexity project type, not a precise quotation - actual addition costs commonly vary significantly once site conditions and structural integration details are confirmed."
  });

  const output: ProjectCalculationOutput = { applied, assumptions, exclusions, anchorMissing, warnings };
  return output;
};
